import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { PageTitle } from '../../../_metronic/layout/core';
import { authUserSelector } from '../../modules/auth';
import {
  ChainType,
  PickingChainEntry,
  PickingChainList,
  PickingChainFormModal,
  listPickingChainEntries,
  createPickingChainEntry,
  updatePickingChainEntry,
  deletePickingChainEntry,
  authorizedPickingChainUsers,
} from '../../modules/picking-chain';

export function PickingChainPage() {
  const intl = useIntl();
  const authUser = useSelector(authUserSelector, shallowEqual);
  const canEdit = authorizedPickingChainUsers.includes(authUser?.email);
  const [entries, setEntries] = useState<PickingChainEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [chainType, setChainType] = useState<ChainType | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PickingChainEntry | null>(
    null
  );

  const chainTypeOptions = [
    {
      value: null as ChainType | null,
      label: intl.formatMessage({ id: 'pickingChain.allChains' }),
    },
    {
      value: 'main' as ChainType,
      label: intl.formatMessage({ id: 'pickingChain.main' }),
    },
    {
      value: 'bvs' as ChainType,
      label: intl.formatMessage({ id: 'pickingChain.bvs' }),
    },
  ];

  async function fetchEntries() {
    try {
      setLoading(true);
      const { data } = await listPickingChainEntries({
        chain_type: chainType || undefined,
      });
      setEntries(data);
    } catch (error) {
      console.warn('Error fetching picking chain entries:', error);
      toast.error(intl.formatMessage({ id: 'pickingChain.fetchError' }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEntries();
  }, [chainType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = (entry: PickingChainEntry) => {
    setEditingEntry(entry);
    setShowFormModal(true);
  };

  const handleCreate = () => {
    setEditingEntry(null);
    setShowFormModal(true);
  };

  const handleSubmit = async (values: Omit<PickingChainEntry, 'id'>) => {
    try {
      if (editingEntry) {
        await updatePickingChainEntry(editingEntry.id, values);
      } else {
        await createPickingChainEntry(values);
      }
      toast.success(intl.formatMessage({ id: 'pickingChain.saveSuccess' }));
      fetchEntries();
    } catch (error: any) {
      console.warn('Error saving picking chain entry:', error);
      const data = error?.response?.data;
      const firstFieldError = data ? Object.values(data).flat()[0] : undefined;
      const errorMessage =
        data?.detail ||
        firstFieldError ||
        intl.formatMessage({ id: 'pickingChain.saveError' });
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePickingChainEntry(id);
      toast.success(intl.formatMessage({ id: 'pickingChain.deleteSuccess' }));
      fetchEntries();
    } catch (error) {
      console.warn('Error deleting picking chain entry:', error);
      toast.error(intl.formatMessage({ id: 'pickingChain.deleteError' }));
    }
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({ id: 'pickingChain.title' })}
      </PageTitle>

      <div className="alert alert-warning d-flex align-items-center mb-5">
        <i className="fa fa-exclamation-triangle text-warning me-3 fs-3" />
        <div>
          <strong>
            {intl.formatMessage({ id: 'pickingChain.warningTitle' })}
          </strong>
          <br />
          {intl.formatMessage({ id: 'pickingChain.warningMessage' })}
        </div>
      </div>

      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-toolbar w-100">
            <div className="w-100 d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex flex-column">
                <label className="form-label fw-bold">
                  {intl.formatMessage({ id: 'pickingChain.chainType' })}
                </label>
                <Select
                  options={chainTypeOptions}
                  value={chainTypeOptions.find(
                    (opt) => opt.value === chainType
                  )}
                  onChange={(option) => setChainType(option?.value || null)}
                  placeholder={intl.formatMessage({
                    id: 'pickingChain.chainType',
                  })}
                  className="w-200px"
                />
              </div>
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-primary ms-auto"
                  onClick={handleCreate}
                >
                  <i className="fa fa-plus me-2" />
                  {intl.formatMessage({ id: 'pickingChain.addEntry' })}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card-body py-4">
          <PickingChainList
            items={entries}
            loading={loading}
            chainType={chainType}
            canEdit={canEdit}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <PickingChainFormModal
        visible={showFormModal}
        entry={editingEntry}
        onClose={() => {
          setShowFormModal(false);
          setEditingEntry(null);
        }}
        onSubmit={handleSubmit}
      />
    </>
  );
}
